import * as React from 'react';
import { CircleMarker, LayersControl, Map as LeafletMap, Marker, Popup, TileLayer, WMSTileLayer } from 'react-leaflet';
import Control from 'react-leaflet-control';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import classnames from 'classnames';
import L from 'leaflet';
import isEqual from 'lodash/isEqual';

import Tooltip from '@components/Tooltip';

import { defaultCenter, defaultZoom } from '@utils/MapUtil';

import 'leaflet.markercluster';

import './style.scss';

interface IMapViewProps {
  baseLayers: IMapLayer[];
  overlayLayers?: IMapLayer[];
  dragging?: boolean;
  scrollWheelZoom?: boolean;
  center: IMapCoordinate;
  markerPosition?: IMapCoordinate;
  zoom?: number;
  onZoomChange?: (newZoomLevel: number) => void;
  onMoveStart?: () => void;
  onMapLoad?: () => void;
  onClick?: (newCoordinates: IMapCoordinate) => void;
  setRef?: (ref: React.Ref<{}>) => void;
  hideLayersControl?: boolean;
  markerPopup?: JSX.Element;
  mapActions?: IMapButton[];
  zoomControlPosition?: MapControlPosition;
  showMarker?: boolean;
  showUserLocation: boolean;
  clusterData?: any;
  markerMode: MarkerMode;
  showTooltipData?: (id: number) => React.ReactNode;
  submissionId?: number;
  onMarkerClick?: (id: number) => void;
  isMarkerDraggable?: boolean;
  isPinMoveWithMap?: boolean;
}

interface IMapViewState {
  userLocation: IMapCoordinate;
  currentZoom?: number;
  mapMovePinPosition?: IMapCoordinate;
}

const defaultMapIcon = new L.Icon({
  iconUrl: '/assets/location_icon.svg',
  iconRetinaUrl: '/assets/location_icon.svg',
  iconSize: new L.Point(30, 30),
  className: 'leaflet-div-icon',
});

const customMapIcon = new L.Icon({
  iconUrl: '/assets/location_custom_icon.svg',
  iconRetinaUrl: '/assets/location_custom_icon.svg',
  iconSize: new L.Point(40, 40),
  className: 'leaflet-div-custom-icon',
});

export default class MapView extends React.Component<IMapViewProps, IMapViewState> {
  static defaultProps: Partial<IMapViewProps> = {
    dragging: true,
    scrollWheelZoom: false,
    overlayLayers: [],
    baseLayers: [],
    center: defaultCenter,
    zoom: defaultZoom,
    hideLayersControl: false,
    onZoomChange: () => null,
    onMoveStart: () => null,
    onClick: () => null,
    onMarkerClick: (id: number) => null,
    markerPopup: null,
    mapActions: null,
    zoomControlPosition: MapControlPosition.BOTTOM_RIGHT,
    showMarker: true,
    markerPosition: null,
    showUserLocation: false,
    markerMode: MarkerMode.CLUSTER,
    isMarkerDraggable: false,
    isPinMoveWithMap: false
  };

  map = null;
  private isDragging = false;

  markerRef: React.RefObject<any>;
  constructor(props: IMapViewProps) {
    super(props);

    this.state = this.getEmptyState();
    this.markerRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.zoomControlPosition) {
      this.map.leafletElement.zoomControl.setPosition(this.props.zoomControlPosition);
    }

    // TODO: It's a code under observation. Do not delete.
    /* if (this.props.center) {
      this.map.leafletElement.setView([
        this.props.center.lat,
        this.props.center.long,
      ]);
    } */

    if (this.props.showUserLocation) {
      this.getLocation();
    }
  }

  componentDidUpdate(prevProps: IMapViewProps) {
    if (this.props.zoomControlPosition) {
      if (this.props.dragging) {
        this.map.leafletElement.zoomControl.enable();
      } else {
        this.map.leafletElement.zoomControl.disable();
      }
    }
    if (this.props.onMapLoad && prevProps === this.props) {
      this.props.onMapLoad();
    }

    if (!isEqual(this.props.center, prevProps.center)) {
      this.map.leafletElement.setView([this.props.center.lat, this.props.center.long]);
    }

    if (!isEqual(this.props.markerPosition, prevProps.markerPosition) && this.state.mapMovePinPosition) {
      this.setState({ mapMovePinPosition: undefined });
    }
  }

  getEmptyState = (): IMapViewState => ({
    userLocation: {
      lat: null,
      long: null,
    },
    currentZoom: this.props.zoom || defaultZoom,
  });

  getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((e) => {
        const { latitude: lat, longitude: long } = e.coords;

        this.setState({
          userLocation: {
            lat,
            long,
          },
        });
      });
    }
  };

  onClick = (e) => {
    if (e.latlng) {
      const newLatLong = {
        lat: e.latlng.lat,
        long: e.latlng.lng,
      };
  
      this.props.onClick(newLatLong);
    }
  };

  onMarkerDragEnd = (e) => {
    const { lat, lng: long } = this.markerRef.current.leafletElement.getLatLng();
    const newLatLong = {
      lat,
      long,
    };

    this.props.onClick(newLatLong);
  };

  onZoomEnd = () => {
    const newZoomLevel = this.map ? this.map.leafletElement.getZoom() : this.props.zoom;
    this.setState((prevState) => ({
      ...prevState,
      currentZoom: newZoomLevel,
    }));
    this.props.onZoomChange(newZoomLevel);
  };

  onDragStart = () => {
    this.isDragging = true;
  };

  onDragEnd = () => {
    this.isDragging = false;
    if (this.state.mapMovePinPosition && this.props.isPinMoveWithMap && this.props.dragging) {
      this.props.onClick(this.state.mapMovePinPosition);
      this.setState({ mapMovePinPosition: undefined });
    }
  };

  onMove = () => {
    if (this.map && this.props.isPinMoveWithMap && this.props.dragging && this.isDragging) {
      const { lat, lng: long } = this.map && this.map.leafletElement.getCenter()

      const newLatLong = {
        lat,
        long,
      };
      this.setState({ mapMovePinPosition: newLatLong})
    }
  };

  onMoveEnd = () => {
    // no-op: commit moved to onDragEnd (Leaflet fires dragend before moveend)
  }

  showCustomMarker = () => {
    const data = this.props.clusterData && this.props.clusterData.find((element) => element.id === this.props.submissionId);
    if (data && data.lat && data.long) {
      this.map.leafletElement.setView([data.lat, data.long], 18);
    } else if (data && data.userPinLat && data.userPinLong) {
      this.map.leafletElement.setView([data.userPinLat, data.userPinLong], 18);
    }

    return '';
  };

  getMarkerLatLong = (pinPosition) => {
    if (this.state.mapMovePinPosition && this.props.isPinMoveWithMap) {
      return [this.state.mapMovePinPosition.lat, this.state.mapMovePinPosition.long];
    } else {
      return pinPosition;
    }
  }

  render() {
    const {
      dragging,
      scrollWheelZoom,
      center,
      markerPosition,
      baseLayers,
      overlayLayers,
      hideLayersControl,
      markerPopup,
      mapActions,
      showMarker,
      zoomControlPosition,
      showUserLocation,
      onMoveStart,
      clusterData,
      submissionId,
      onMarkerClick,
      isMarkerDraggable,
    } = this.props;
    const mapCenter: L.LatLngExpression = [center.lat, center.long];
    const pinPosition: L.LatLngExpression =
      markerPosition && markerPosition.lat && markerPosition.long
        ? [markerPosition.lat, markerPosition.long]
        : mapCenter;
    const { userLocation, currentZoom } = this.state;

    return (
      <div
        className={classnames('map-container', {
          'map-container--hide-layer-control': hideLayersControl,
        })}
      >
        <LeafletMap
          ref={(m) => {
            this.map = m;
            // tslint:disable-next-line: no-unused-expression
            this.props.setRef && this.props.setRef(m as any);
          }}
          dragging={dragging}
          scrollWheelZoom={scrollWheelZoom}
          zoom={currentZoom}
          center={mapCenter}
          maxZoom={18}
          zoomControl={!!zoomControlPosition}
          onClick={this.onClick}
          onzoomend={this.onZoomEnd}
          style={{
            height: this.props.markerMode === MarkerMode.CLUSTER ? '100%' : '100vh',
          }}
          onmove={this.onMove}
          onmovestart={onMoveStart ? onMoveStart : undefined}
          onmoveend={this.onMoveEnd}
          ondragstart={this.onDragStart}
          ondragend={this.onDragEnd}
        >
          {mapActions &&
            mapActions.map((mapButton, index) => {
              const actionLabel = mapButton.ariaLabel || mapButton.tooltipText;

              return (
                <Control key={index} position={mapButton.position}>
                  <Tooltip tooltipText={mapButton.tooltipText}>
                    <button
                      type="button"
                      className={classnames('map-container__control', mapButton.className)}
                      aria-label={actionLabel}
                      title={actionLabel}
                      disabled={mapButton.disabled}
                      onClick={mapButton.onClick}
                    />
                  </Tooltip>
                </Control>
              );
            })}
          <LayersControl position="bottomleft">
            {baseLayers &&
              baseLayers.map((baseLayer, index) => (
                <LayersControl.BaseLayer
                  checked={baseLayer.checked}
                  name={baseLayer.label || baseLayer.name}
                  key={index}
                >
                  <TileLayer url={baseLayer.url} />
                </LayersControl.BaseLayer>
              ))}
            {overlayLayers &&
              overlayLayers.map((overlayLayer, index) => (
                <LayersControl.Overlay
                  key={index}
                  checked={overlayLayer.checked}
                  name={overlayLayer.label || overlayLayer.name}
                >
                  <WMSTileLayer
                    layers={overlayLayer.name}
                    url={overlayLayer.url}
                    format="image/png"
                    maxZoom={19}
                    transparent={true}
                  />
                </LayersControl.Overlay>
              ))}
          </LayersControl>
          {showUserLocation && userLocation.lat && (
            <CircleMarker
              center={[userLocation.lat, userLocation.long]}
              radius={10}
              color="blue"
              weight={15}
              opacity={0.2}
              fillColor="blue"
              fillOpacity={1}
            />
          )}
          {showMarker && (
            <Marker
              draggable={isMarkerDraggable}
              ondragend={this.onMarkerDragEnd}
              position={this.getMarkerLatLong(pinPosition)}
              ref={this.markerRef}
            >
              {markerPopup && <Popup>{markerPopup}</Popup>}
            </Marker>
          )}
          {this.props.markerMode === MarkerMode.PIN &&
            clusterData &&
            clusterData.map((point, key) => {
              const lat = point.userPinLat ?? point.lat;
              const long = point.userPinLong ?? point.long;

              const showMarker = ![null, undefined].includes(lat) && ![null, undefined].includes(long);

              return showMarker ? (
                <Marker
                  onClick={() => onMarkerClick(point.id)}
                  key={key}
                  icon={submissionId && submissionId === point.id ? customMapIcon : defaultMapIcon}
                  position={[parseFloat(lat), parseFloat(long)]}
                >
                  <Popup>
                    {this.props.showTooltipData ? this.props.showTooltipData(point.id) : <p>{point.location}</p>}
                  </Popup>
                </Marker>
              ) : null;
            })}
          {this.props.markerMode === MarkerMode.CLUSTER && clusterData && (
            <MarkerClusterGroup disableClusteringAtZoom={15} className={'marker-cluster'}>
              {clusterData.map((point, key) => {
                const lat = point.userPinLat ?? point.lat;
                const long = point.userPinLong ?? point.long;

                const showMarker = ![null, undefined].includes(lat) && ![null, undefined].includes(long);

                return showMarker ? (
                  <Marker
                    key={key}
                    onClick={() => onMarkerClick(point.id)}
                    icon={submissionId && submissionId === point.id ? customMapIcon : defaultMapIcon}
                    position={[parseFloat(lat), parseFloat(long)]}
                  >
                    <Popup>
                      {this.props.showTooltipData ? this.props.showTooltipData(point.id) : <p>{point.location}</p>}
                    </Popup>
                  </Marker>
                ) : null;
              })}
            </MarkerClusterGroup>
          )}
          {this.props.submissionId && this.showCustomMarker()}
        </LeafletMap>
      </div>
    );
  }
}
